
import Vue from "vue";
import { Component, Watch, Prop } from "vue-property-decorator";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import TooltipLabel from "@/components/general/TooltipLabel.vue";
import { AxiosError } from "axios";
import { Emit } from "@/api/apiHelper";
import { censorDataIfNeeded } from "@/utils/sensitiveNumbersUtils";
import CensorableChart from "@/components/chart/CensorableChart.vue";
import {
  generateGradientChartBackground,
  getTimeScales,
} from "@/utils/chartUtils";
import { yAxisInteractionLine } from "@/plugins/chartjs";

@Component({
  components: { TooltipLabel },
})
export default class BalanceInUseChart extends CensorableChart {
  // 🎨 Possible views that can be shown in this component
  private ComponentsToShow = {
    Loading: 0,
    Placeholder: 1,
    Chart: 2,
  };

  // 📦 The currently displayed component
  private currentlyShownComponent: number = this.ComponentsToShow.Loading;

  // 📦 Balance in use over time
  private balanceInUseDataset: number[] = [];

  // 📦 Dates for each balance in use check-up
  private dateLabels: Date[] = [];

  // 📦 The selected portfolio chart filter in days
  @Prop({ default: 1 })
  public selectedFilterInDays!: number;

  // 📦📈 Chart which displays the balance in use over time
  private balanceInUseChart: Chart | null = null;

  // 📦 The placeholder content that should be displayed
  private placeholder = {};

  // 🗑 Temporary variable that prevents double request for balance in use
  private latestFilterInDays: number = 0;

  /**
   * 👂 Listener used in order to determine the selected chart filter mode
   */
  @Watch("selectedFilterInDays")
  private onDaysFilterChanged(val: number, oldVal?: number) {
    if (this.latestFilterInDays != val) {
      this.latestFilterInDays = val;
      this.fetchBotBalanceInUseOverTime();
    }
  }

  protected mounted() {
    this.fetchBotBalanceInUseOverTime();
  }

  /**
   * 🚀 Fetches the balance in use for the current user
   */
  private async fetchBotBalanceInUseOverTime() {
    Vue.prototype.$api
      .get("/api/v1/balanceinuse", this.getRequestParams(), Emit.None)
      .then((response: any) => {
        // Reset data sets
        this.balanceInUseDataset = [];
        this.dateLabels = [];

        this.dateLabels = Object.keys(response.data.values).map(
          (value: String, index: Number, array: String[]) => {
            return new Date(
              Number(value) * 1000 - new Date().getTimezoneOffset() * 60
            );
          }
        );
        this.balanceInUseDataset = Object.values(response.data.values);

        if (
          this.balanceInUseDataset != null &&
          this.balanceInUseDataset.length > 0
        ) {
          // TODO: 21/08/2021 - Replace this with a more appropriate way to provide data
          if (this.balanceInUseDataset.length > 1) {
            this.$emit(
              "initial-balance-for-period",
              this.balanceInUseDataset[0]
            );
          }

          this.currentlyShownComponent = this.ComponentsToShow.Chart;
          this.createChart();
        } else {
          this.placeholder = {
            title: "Your account is still fresh",
            description:
              "Your balance in use will be updated once you have at least one running bot",
          };
          this.currentlyShownComponent = this.ComponentsToShow.Placeholder;
        }
      })
      .catch((error: AxiosError) => {
        this.placeholder = {
          title: "Error while fetching your balance in use",
          description: "We are sorry about that! Try again in a bit",
          button: true,
        };
        this.currentlyShownComponent = this.ComponentsToShow.Placeholder;
      });
  }

  /**
   * 🔨 Returns the request parameters needed when requesting the balance in use
   */
  private getRequestParams(): any {
    if (this.selectedFilterInDays != -1) {
      return { days: this.selectedFilterInDays };
    }
  }

  /**
   * 🎨 Draws a dynamic chart on the canvas which is responsible for displaying the balance in use
   */
  protected createChart() {
    if (this.balanceInUseChart == null) {
      let container: any = document.getElementById(
        "container-balance-in-use-chart"
      );

      this.balanceInUseChart = new Chart(container, {
        type: "line",
        data: {
          labels: this.dateLabels,
          datasets: generateGradientChartBackground(
            "Balance in use",
            this.$refs.containerBalanceInUseChart as any,
            this.balanceInUseDataset
          ),
        },

        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: getTimeScales(this.$vuetify, this.selectedFilterInDays!! == 1),
            y: this.getYAxes(),
          },
          interaction: {
            intersect: false,
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              mode: "index",
              titleFont: { family: "Aeroport" },
              bodyFont: { family: "Aeroport" },
              callbacks: {
                label: (context: any) => {
                  return (
                    context.dataset.label +
                    ": $" +
                    censorDataIfNeeded(Number(context.parsed.y).toFixed(2))
                  );
                },
              },
            },
          },
        },
        plugins: [yAxisInteractionLine(this.$vuetify)],
      });
    } else {
      this.balanceInUseChart.data.datasets = generateGradientChartBackground(
        "Balance in use",
        this.$refs.containerBalanceInUseChart as any,
        this.balanceInUseDataset
      );
      this.balanceInUseChart.data.labels = this.dateLabels;
      this.balanceInUseChart.options.scales!!.x = getTimeScales(
        this.$vuetify,
        this.selectedFilterInDays!! == 1
      );
      this.balanceInUseChart.options.scales!!.y = this.getYAxes();
      this.balanceInUseChart.update();
    }
  }

  /**
   * 🔨 Calculates a dynamic y axis based on the provided dataset
   */
  private getYAxes(): any {
    // Minimal value of the dataset
    let minValue: number = Math.min(...this.balanceInUseDataset);

    // Maximum value of the dataset
    let maxValue: number = Math.max(...this.balanceInUseDataset);

    // Boundary which is equal to 5% of the difference between the maximum and minimum value of the dataset
    let chartBoundary: number =
      maxValue - minValue == 0
        ? Math.ceil(maxValue * 0.1)
        : Math.ceil((maxValue - minValue) * 0.05);

    // Minimum y value that should be displayed on the chart
    let minDisplayValue: number = this.getMinDisplayValue(
      minValue,
      maxValue,
      chartBoundary
    );

    // Maximum y value that should be displayed on the chart
    let maxDisplayValue: number = this.getMaxDisplayValue(
      minValue,
      maxValue,
      chartBoundary
    );

    // Step size which is equal to 15% of the difference between the maximum and minimum display value of the dataset
    let stepSize: number =
      maxDisplayValue - minDisplayValue == 0
        ? 1
        : (maxDisplayValue - minDisplayValue) * 0.15;

    return {
      ticks: {
        min: minDisplayValue,
        max: maxDisplayValue,
        stepSize: stepSize,
        maxTicksLimit: 8,
        callback: this.getYAxisDataFormater,
        color: this.$vuetify.theme.currentTheme.chartLabel,
      },
      grid: {
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
      stacked: true,
    };
  }

  /**
   * 🔨 Callback function that is used to format the y-axis values
   */
  private getYAxisDataFormater(
    value: number,
    index: number,
    values: number[]
  ): string {
    return (
      "$" +
      censorDataIfNeeded(
        Math.max(...values) - Math.min(...values) >= 50
          ? value.toFixed(0)
          : value.toFixed(2)
      )
    );
  }

  /**
   * 🏗 Calculates and returns the minimum display value
   */
  private getMinDisplayValue(
    minValue: number,
    maxValue: number,
    chartBoundary: number
  ) {
    if (minValue <= 0) {
      return 0;
    }

    if (maxValue - minValue < 1) {
      return minValue + -chartBoundary > 0
        ? Math.floor(minValue + -chartBoundary)
        : 0;
    }

    return Math.floor(minValue);
  }

  /**
   * 🏗 Calculates and returns the maximum display value
   */
  private getMaxDisplayValue(
    minValue: number,
    maxValue: number,
    chartBoundary: number
  ) {
    if (maxValue <= 0) {
      return 5;
    }

    if (maxValue - minValue < 1) {
      return maxValue + chartBoundary > 0
        ? Math.ceil(maxValue + chartBoundary)
        : 0;
    }

    return Math.ceil(maxValue);
  }
}
